import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Space, Tag, Typography, message } from 'antd';
import { getWechatConfig, updateWechatConfig, type AdminWechatConfig } from '../services/api';

const defaultAuthBaseURL = 'https://api.weixin.qq.com';

const WechatConfig: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasSecret, setHasSecret] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getWechatConfig();
      if (res.code !== 0) {
        message.error(res.message || '加载微信配置失败');
        return;
      }
      const config = res.data as AdminWechatConfig;
      setHasSecret(config.has_app_secret);
      form.setFieldsValue({
        app_id: config.app_id || '',
        app_secret: '',
        universal_link: config.universal_link || '',
        auth_base_url: config.auth_base_url || defaultAuthBaseURL,
      });
    } catch (error) {
      message.error('加载微信配置失败');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const onFinish = async (values: {
    app_id: string;
    app_secret?: string;
    universal_link?: string;
    auth_base_url?: string;
  }) => {
    setSaving(true);
    try {
      const payload = {
        app_id: (values.app_id || '').trim(),
        app_secret: (values.app_secret || '').trim(),
        universal_link: (values.universal_link || '').trim(),
        auth_base_url: (values.auth_base_url || '').trim() || defaultAuthBaseURL,
      };

      const res = await updateWechatConfig(payload);
      if (res.code !== 0) {
        message.error(res.message || '保存微信配置失败');
        return;
      }

      message.success('微信登录配置已保存');
      setHasSecret(Boolean(res.data?.has_app_secret));
      form.setFieldValue('app_secret', '');
    } catch (error) {
      message.error('保存微信配置失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        微信一键授权登录配置
      </Typography.Title>

      <Card loading={loading}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="此配置会直接影响 App 端微信一键登录。保存后后端会立即使用新配置进行 code 换取 openid。"
          />

          <div>
            当前密钥状态：
            {hasSecret ? <Tag color="green">已配置 AppSecret</Tag> : <Tag color="red">未配置 AppSecret</Tag>}
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
          >
            <Form.Item
              label="微信 AppID"
              name="app_id"
              rules={[{ required: true, message: '请输入微信 AppID' }]}
            >
              <Input placeholder="例如：wx1234567890abcdef" />
            </Form.Item>

            <Form.Item
              label="微信 AppSecret"
              name="app_secret"
              extra="留空表示保留当前密钥，不会覆盖。"
            >
              <Input.Password placeholder="请输入新的 AppSecret（可留空）" />
            </Form.Item>

            <Form.Item
              label="iOS Universal Link"
              name="universal_link"
              extra="iOS 微信登录需要，Android 可留空。"
            >
              <Input placeholder="例如：https://your.domain.com/link/" />
            </Form.Item>

            <Form.Item
              label="微信授权接口基础地址"
              name="auth_base_url"
              rules={[{ required: true, message: '请输入微信授权接口基础地址' }]}
            >
              <Input placeholder={defaultAuthBaseURL} />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" loading={saving}>
                保存配置
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Card>
    </div>
  );
};

export default WechatConfig;
